// ============================================================================
// RECEIPT VOUCHER TYPES (ADR-0054) — the one-step receipt, mirror of payment.types.ts.
// ============================================================================

export const RECEIPT_LINE_TYPES = {
  /** Cr the party ledger against a debit-side open item: an invoice, or a supplier's debit balance. */
  BILL_SETTLEMENT: 'BILL_SETTLEMENT',
  /** Cr the party ledger as a new item — customer advance or supplier refund, set off later. */
  ON_ACCOUNT: 'ON_ACCOUNT',
  /** Any nominal account; isCredit adds to the receipt, a debit is a deduction off the cash. */
  GL_LINE: 'GL_LINE',
} as const;

export const isReceiptPartyLine = (t: string): boolean =>
  t === RECEIPT_LINE_TYPES.BILL_SETTLEMENT || t === RECEIPT_LINE_TYPES.ON_ACCOUNT;

export const RECEIVE_AS = {
  CUSTOMER_ADVANCE: 'CUSTOMER_ADVANCE',
  SUPPLIER_REFUND: 'SUPPLIER_REFUND',
} as const;

export interface ReceiptVoucherLineDto {
  id: string;
  lineNo: number;
  lineType: string;
  ledgerId?: string | null;
  ledgerCode?: string | null;
  ledgerName?: string | null;
  partyId?: string | null;
  partyName?: string | null;
  businessUnitId?: string | null;
  businessUnitName?: string | null;
  settledOpenItemId?: string | null;
  settledReferenceNo?: string | null;
  receiveAs?: string | null;
  /** Base, as posted. */
  amount: number;
  /** As typed in the receipt currency; null in home currency. */
  fcAmount?: number | null;
  /** Settlements: realized FX, gain positive. */
  realizedFxAmount: number;
  settledCurrency?: string | null;
  settledFcAmount?: number | null;
  /** TRUE = credit (adds to the receipt), FALSE = debit (a deduction). */
  isCredit: boolean;
  openItemId?: string | null;
  narration?: string | null;
  /** ADR-0060: the part of the bill closed but kept open as a short-paid item — bill currency. */
  shortPaidFcAmount?: number | null;
  /** The same in base, at the bill's booked rate. */
  shortPaidAmount?: number | null;
  shortPaidReasonCode?: string | null;
  shortPaidReasonLabel?: string | null;
  shortPaidRemarks?: string | null;
  shortPaidOpenItemId?: string | null;
}

export interface ReceiptVoucherDto {
  id: string;
  entityId: string;
  businessUnitId?: string | null;
  businessUnitName?: string | null;
  receiptNumber?: string | null;
  voucherTypeId?: string | null;
  receiptDate: string;
  partyId?: string | null;
  partyName?: string | null;
  depositToLedgerId: string;
  depositToLedgerName?: string | null;
  currencyCode?: string | null;
  exchangeRate: number;
  baseCurrencyCode?: string | null;
  grossAmount: number;
  deductionTotal: number;
  netReceived: number;
  instrumentType?: string | null;
  instrumentNumber?: string | null;
  instrumentDate?: string | null;
  drawerBank?: string | null;
  narration?: string | null;
  docStatus: string;
  transactionId?: string | null;
  statusName?: string | null;
  rowVersion: number;
  createdAt: string;
  createdByName?: string | null;
  lines: ReceiptVoucherLineDto[];
}

export interface CreateReceiptVoucherLineRequest {
  lineType: string;
  ledgerId?: string | null;
  partyId?: string | null;
  businessUnitId?: string | null;
  settledOpenItemId?: string | null;
  receiveAs?: string | null;
  /** In the receipt currency. Positive — a settlement may be 0 when its bill is short-paid in full (ADR-0060). */
  amount: number;
  /** Cross-currency settlement: the item-currency amount discharged. */
  settledFcAmount?: number | null;
  /** Settlement (ADR-0060): bill-currency amount the customer deducted — closes the bill, stays open as a short-paid item. */
  shortPaidAmount?: number | null;
  /** Required with shortPaidAmount — DeductionReasonDto.value. */
  shortPaidReasonCode?: string | null;
  shortPaidRemarks?: string | null;
  /** GL_LINE: TRUE adds (Cr), FALSE reduces (Dr); null = from the ledger's nature. */
  isCredit?: boolean | null;
  narration?: string | null;
}

export interface CreateReceiptVoucherRequest {
  receiptDate: string;
  partyId?: string | null;
  depositToLedgerId: string;
  currencyCode?: string | null;
  exchangeRate: number;
  instrumentType?: string | null;
  instrumentNumber?: string | null;
  instrumentDate?: string | null;
  drawerBank?: string | null;
  narration?: string | null;
  lines: CreateReceiptVoucherLineRequest[];
}

/** A party that owes the entity — debit-side open items on either ledger. */
export interface ReceivablePartyDto {
  partyId: string;
  partyCode: string;
  partyName: string;
  partyType?: string | null;
  amountDue: number;
  openItemCount: number;
  hasCustomerBills: boolean;
  hasSupplierDebits: boolean;
}

export interface ReceiptVoucherQueryParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  partyId?: string;
  fromDate?: string;
  toDate?: string;
  includeCancelled?: boolean;
}
